import type * as THREE from 'three';
import type RAPIER from '@dimforge/rapier3d-compat';

export interface FeatureContext {
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  readonly renderer: THREE.WebGLRenderer;
  readonly physics: RAPIER.World;
}

export interface FeatureModule {
  readonly id: string;
  initialize?(context: FeatureContext): void | Promise<void>;
  update?(deltaSeconds: number, context: FeatureContext): void;
  dispose?(): void;
}

export class FeatureRegistry {
  private readonly modules = new Map<string, FeatureModule>();
  private context?: FeatureContext;

  register(module: FeatureModule): this {
    if (this.modules.has(module.id)) {
      throw new Error(`Feature module "${module.id}" is already registered.`);
    }
    this.modules.set(module.id, module);
    return this;
  }

  async initialize(context: FeatureContext): Promise<void> {
    this.context = context;
    for (const module of this.modules.values()) await module.initialize?.(context);
  }

  update(deltaSeconds: number): void {
    if (!this.context) return;
    for (const module of this.modules.values()) module.update?.(deltaSeconds, this.context);
  }

  dispose(): void {
    for (const module of [...this.modules.values()].reverse()) module.dispose?.();
    this.context = undefined;
  }
}
